/**
 * CAPM CALCULATOR - Capital Asset Pricing Model
 * 
 * Implementação completa do CAPM com transparência total dos componentes.
 * Resolve inconsistências identificadas na auditoria metodológica P0.1.
 * 
 * Fórmula completa do Ke:
 * Ke = Rf + β×(ERP + CRP?) + Size Premium + Specific Risk
 * 
 * Onde:
 * - Rf = Risk-free rate (Selic ou US Treasury 10Y)
 * - β = Beta (desalavancado ou alavancado)
 * - ERP = Equity Risk Premium (prêmio de risco de mercado global)
 * - CRP = Country Risk Premium (prêmio de risco Brasil)
 * - Size Premium = Prêmio por tamanho (small cap)
 * - Specific Risk = Risco específico da empresa (size, liquidity, keyPerson, execution)
 * 
 * Metodologias de CRP:
 * - multiplicative: CRP embutido no β×(ERP+CRP) - usado por alguns analistas
 * - additive: CRP separado - recomendado por Damodaran para emergentes
 * 
 * @module utils/capmCalculator
 */

import type {
  CAPMComponents,
  KeBreakdown,
  WACCBreakdown,
  SpecificRiskBreakdown,
  CRPMethodology,
  RfMode,
} from '../types/valuation';

// ============================================================================
// INTERFACES AUXILIARES
// ============================================================================

export interface CAPMParams {
  /** Taxa livre de risco (ex: Selic 0.1175 = 11.75%) */
  rf: number;
  
  /** Modo da taxa livre de risco */
  rfMode: RfMode;
  
  /** Beta da empresa */
  beta: number;
  
  /** Equity Risk Premium - prêmio de risco de mercado global */
  erp: number;
  
  /** Country Risk Premium - prêmio de risco Brasil */
  crp: number;
  
  /** Metodologia de aplicação do CRP */
  crpMethodology: CRPMethodology;
  
  /** Size Premium - prêmio por tamanho (small cap) */
  sizePremium?: number;
  
  /** Specific Risk - risco específico da empresa */
  specificRisk?: number;
  
  /** Breakdown detalhado do Specific Risk (opcional) */
  specificRiskBreakdown?: SpecificRiskBreakdown;
}

export interface BetaLeveringParams {
  /** Beta desalavancado (asset beta) */
  betaUnlevered: number;
  
  /** Relação Dívida/Equity (D/E) */
  debtToEquity: number;
  
  /** Taxa de imposto corporativo */
  taxRate: number;
}

export interface WACCParams {
  /** Cost of Equity (Ke) */
  ke: number;
  
  /** Breakdown detalhado do Ke */
  keBreakdown: KeBreakdown;
  
  /** Peso do equity na estrutura de capital (E/(D+E)) */
  equityWeight: number;
  
  /** Cost of Debt (Kd) - antes de impostos */
  kd?: number;
  
  /** Taxa de imposto para tax shield */
  taxRate?: number;
}

export interface CAPMValidationResult {
  /** Se a validação passou */
  isValid: boolean;
  
  /** Warnings encontrados */
  warnings: string[];
  
  /** Erros críticos */
  errors: string[];
  
  /** Sugestões de correção */
  suggestions: string[];
}

// ============================================================================
// CÁLCULO DO Ke (COST OF EQUITY)
// ============================================================================

/**
 * Calcula o Cost of Equity (Ke) usando CAPM com metodologia completa
 * 
 * @param params - Parâmetros do CAPM
 * @returns Componentes detalhados do CAPM
 * 
 * @example
 * ```typescript
 * const capm = calculateKe({
 *   rf: 0.1175,
 *   rfMode: 'local',
 *   beta: 1.2,
 *   erp: 0.055,
 *   crp: 0.035,
 *   crpMethodology: 'additive',
 *   sizePremium: 0.03,
 *   specificRisk: 0.05
 * });
 * console.log(capm.ke); // 0.2641 (26.41%)
 * ```
 */
export function calculateKe(params: CAPMParams): CAPMComponents {
  const {
    rf,
    beta,
    erp,
    crp,
    crpMethodology,
    sizePremium = 0,
    specificRisk = 0,
  } = params;

  let ke: number;
  
  if (crpMethodology === 'multiplicative') {
    // CRP embutido: Ke = Rf + β×(ERP + CRP) + Size + Specific
    const marketRiskPremium = erp + crp;
    const betaTimesMarketRisk = beta * marketRiskPremium;
    ke = rf + betaTimesMarketRisk + sizePremium + specificRisk;
  } else {
    // CRP aditivo (recomendado Damodaran): Ke = Rf + β×ERP + CRP + Size + Specific
    const betaTimesERP = beta * erp;
    ke = rf + betaTimesERP + crp + sizePremium + specificRisk;
  }

  return {
    rf,
    beta,
    erp,
    crp,
    crpMethodology,
    sizePremium,
    specificRisk,
    ke,
  };
}

/**
 * Cria um breakdown detalhado do Ke para auditoria
 * 
 * @param components - Componentes CAPM calculados
 * @param specificRiskBreakdown - Breakdown opcional do risco específico
 * @returns Breakdown estruturado do Ke
 */
export function createKeBreakdown(
  components: CAPMComponents,
  specificRiskBreakdown?: SpecificRiskBreakdown
): KeBreakdown {
  const { rf, beta, erp, crp, crpMethodology, sizePremium, specificRisk } = components;

  // Determinar fonte do Rf
  const rfSource = rf > 0.08 ? 'Selic' : 'US Treasury 10Y';

  // Calcular β × market risk
  let betaTimesMarketRisk: number;
  let marketRiskLabel: string;

  if (crpMethodology === 'multiplicative') {
    betaTimesMarketRisk = beta * (erp + crp);
    marketRiskLabel = `β × (ERP + CRP) = ${beta.toFixed(2)} × (${(erp * 100).toFixed(1)}% + ${(crp * 100).toFixed(1)}%)`;
  } else {
    betaTimesMarketRisk = beta * erp;
    marketRiskLabel = `β × ERP = ${beta.toFixed(2)} × ${(erp * 100).toFixed(1)}%`;
  }

  const breakdown: KeBreakdown = {
    rf: {
      valor: rf,
      fonte: rfSource,
      label: `Risk-free rate (${rfSource})`,
    },
    beta_x_market_risk: {
      beta,
      erp,
      valor: betaTimesMarketRisk,
      label: marketRiskLabel,
    },
    sizePremium: {
      valor: sizePremium,
      label: 'Size Premium (small cap)',
    },
    specificRisk: {
      valor: specificRisk,
      label: 'Specific Risk',
      breakdown: specificRiskBreakdown || {
        size: 0,
        liquidity: 0,
        keyPerson: 0,
        execution: 0,
      },
    },
    total: {
      valor: components.ke,
      label: 'Cost of Equity (Ke)',
    },
  };

  // Adicionar CRP aditivo se aplicável
  if (crpMethodology === 'additive') {
    breakdown.beta_x_market_risk.crp = crp;
    breakdown.crp_additive = {
      valor: crp,
      label: `Country Risk Premium (additive)`,
    };
  } else {
    breakdown.beta_x_market_risk.crp = crp;
  }

  return breakdown;
}

// ============================================================================
// CÁLCULO DO WACC
// ============================================================================

/**
 * Calcula o WACC (Weighted Average Cost of Capital)
 * 
 * @param params - Parâmetros do WACC
 * @returns Breakdown detalhado do WACC
 * 
 * @example
 * ```typescript
 * const wacc = calculateWACC({
 *   ke: 0.2641,
 *   keBreakdown: keBreakdownObj,
 *   equityWeight: 1.0, // 100% equity
 * });
 * console.log(wacc.wacc.valor); // 0.2641 (26.41%)
 * ```
 */
export function calculateWACC(params: WACCParams): WACCBreakdown {
  const { ke, keBreakdown, equityWeight, kd, taxRate } = params;

  const debtWeight = 1 - equityWeight;
  const debtToEquity = equityWeight > 0 ? debtWeight / equityWeight : 0;

  let waccValue: number;
  let waccFormula: string;

  if (debtWeight > 0 && kd !== undefined && taxRate !== undefined) {
    // Empresa com dívida: WACC = Ke×(E/(D+E)) + Kd×(1-tax)×(D/(D+E))
    const kdAfterTax = kd * (1 - taxRate);
    const keContribution = ke * equityWeight;
    const kdContribution = kdAfterTax * debtWeight;
    waccValue = keContribution + kdContribution;
    waccFormula = `Ke×E/(D+E) + Kd×(1-tax)×D/(D+E)`;

    return {
      ke: {
        valor: ke,
        peso: equityWeight,
        contribuicao: keContribution,
        breakdown: keBreakdown,
      },
      capitalStructure: {
        debtToEquity,
        equityWeight,
        debtWeight,
      },
      kd: {
        valor: kd,
        valorAfterTax: kdAfterTax,
        peso: debtWeight,
        contribuicao: kdContribution,
      },
      wacc: {
        valor: waccValue,
        formula: waccFormula,
      },
    };
  } else {
    // Empresa 100% equity: WACC = Ke
    waccValue = ke;
    waccFormula = 'Ke (100% equity)';

    return {
      ke: {
        valor: ke,
        peso: equityWeight,
        contribuicao: ke,
        breakdown: keBreakdown,
      },
      capitalStructure: {
        debtToEquity: 0,
        equityWeight: 1.0,
        debtWeight: 0,
      },
      wacc: {
        valor: waccValue,
        formula: waccFormula,
      },
    };
  }
}

// ============================================================================
// BETA LEVERING / UNLEVERING
// ============================================================================

/**
 * Calcula o Beta alavancado (levered) a partir do Beta desalavancado
 * 
 * Fórmula Hamada: βL = βU × [1 + (1 - tax) × (D/E)]
 * 
 * @param params - Parâmetros de levering
 * @returns Beta alavancado
 * 
 * @example
 * ```typescript
 * const betaLevered = calculateBetaLevered({
 *   betaUnlevered: 1.0,
 *   debtToEquity: 0.3,
 *   taxRate: 0.34
 * });
 * console.log(betaLevered); // 1.198
 * ```
 */
export function calculateBetaLevered(params: BetaLeveringParams): number {
  const { betaUnlevered, debtToEquity, taxRate } = params;
  
  // Hamada formula: βL = βU × [1 + (1 - tax) × (D/E)]
  const leverageFactor = 1 + (1 - taxRate) * debtToEquity;
  return betaUnlevered * leverageFactor;
}

/**
 * Calcula o Beta desalavancado (unlevered) a partir do Beta alavancado
 * 
 * Fórmula inversa: βU = βL / [1 + (1 - tax) × (D/E)]
 * 
 * @param betaLevered - Beta alavancado (observado)
 * @param debtToEquity - Relação Dívida/Equity
 * @param taxRate - Taxa de imposto
 * @returns Beta desalavancado
 */
export function calculateBetaUnlevered(
  betaLevered: number,
  debtToEquity: number,
  taxRate: number
): number {
  const leverageFactor = 1 + (1 - taxRate) * debtToEquity;
  return betaLevered / leverageFactor;
}

// ============================================================================
// VALIDAÇÃO CAPM
// ============================================================================

/**
 * Valida componentes CAPM e identifica inconsistências metodológicas
 * 
 * Validações:
 * 1. CRP Duplication Risk: Se Rf = Selic, CRP pode estar duplicado
 * 2. Beta sensato: 0.5 <= β <= 2.5
 * 3. ERP razoável: 4% <= ERP <= 10%
 * 4. CRP razoável: 1% <= CRP <= 8%
 * 5. Specific Risk não excessivo: <= 10%
 * 
 * @param components - Componentes CAPM a validar
 * @returns Resultado da validação com warnings/errors
 */
export function validateCAPM(components: CAPMComponents): CAPMValidationResult {
  const { rf, beta, erp, crp, crpMethodology, sizePremium, specificRisk } = components;

  const warnings: string[] = [];
  const errors: string[] = [];
  const suggestions: string[] = [];

  // Validação 1: CRP Duplication Risk
  if (rf > 0.08) {
    // Provavelmente Selic (Brasil)
    if (crpMethodology === 'multiplicative') {
      warnings.push(
        `⚠️ RISCO DE DUPLICAÇÃO CRP: Rf = ${(rf * 100).toFixed(2)}% (Selic já contém CRP). ` +
        `Metodologia 'multiplicative' pode aplicar CRP novamente via β×(ERP+CRP).`
      );
      suggestions.push(
        `Considere usar rfMode='us_treasury' + crpMethodology='additive' para evitar duplicação, ` +
        `OU use Selic com β×ERP puro (sem CRP no market risk).`
      );
    }
  }

  // Validação 2: Beta sensato
  if (beta < 0.5 || beta > 2.5) {
    warnings.push(
      `⚠️ Beta fora do intervalo comum: β = ${beta.toFixed(2)}. ` +
      `Valores típicos: 0.5-2.5. Verifique se é beta levered ou unlevered.`
    );
  }

  // Validação 3: ERP razoável
  if (erp < 0.04 || erp > 0.10) {
    warnings.push(
      `⚠️ ERP fora do intervalo Damodaran: ${(erp * 100).toFixed(1)}%. ` +
      `Range esperado: 4%-10%. ERP global típico: 5.5%-6.5%.`
    );
  }

  // Validação 4: CRP razoável
  if (crp < 0.01 || crp > 0.08) {
    warnings.push(
      `⚠️ CRP Brasil atípico: ${(crp * 100).toFixed(1)}%. ` +
      `Range histórico EMBI+: 1%-8%. Verificar fonte (EMBI+ spread).`
    );
  }

  // Validação 5: Specific Risk não excessivo
  if (specificRisk > 0.10) {
    warnings.push(
      `⚠️ Specific Risk elevado: ${(specificRisk * 100).toFixed(1)}%. ` +
      `Valores >10% podem indicar dupla contagem com Size Premium ou KPD.`
    );
    suggestions.push(
      `Verifique se Key Person Risk não está duplicado entre Specific Risk e Equity Haircut (KPD).`
    );
  }

  // Validação 6: Size Premium sensato
  if (sizePremium > 0.05) {
    warnings.push(
      `⚠️ Size Premium alto: ${(sizePremium * 100).toFixed(1)}%. ` +
      `Tabelas Ibbotson SBBI: small cap ~3%-4%. Verifique se apropriado.`
    );
  }

  // Validação 7: Ke total razoável
  const ke = components.ke;
  if (ke > 0.40) {
    errors.push(
      `❌ Ke excessivo: ${(ke * 100).toFixed(1)}%. ` +
      `Valores >40% são raros e podem indicar erro de cálculo ou dupla contagem.`
    );
  } else if (ke < 0.08) {
    errors.push(
      `❌ Ke muito baixo: ${(ke * 100).toFixed(1)}%. ` +
      `Valores <8% abaixo da Selic indicam erro (Ke deve ser > Rf).`
    );
  }

  const isValid = errors.length === 0;

  return {
    isValid,
    warnings,
    errors,
    suggestions,
  };
}

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Formata número como percentual
 */
export function formatPercent(value: number, decimals: number = 2): string {
  return `${(value * 100).toFixed(decimals)}%`;
}

/**
 * Converte taxa nominal para real usando Fisher equation
 * 
 * (1 + nominal) = (1 + real) × (1 + inflação)
 * real = [(1 + nominal) / (1 + inflação)] - 1
 */
export function nominalToReal(nominal: number, inflation: number): number {
  return ((1 + nominal) / (1 + inflation)) - 1;
}

/**
 * Converte taxa real para nominal usando Fisher equation
 * 
 * nominal = (1 + real) × (1 + inflação) - 1
 */
export function realToNominal(real: number, inflation: number): number {
  return (1 + real) * (1 + inflation) - 1;
}
